import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, Container, Grid, IconButton, Paper, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useMemo, useState } from 'react';
import { BookingStatus } from '../../../happypaws-shared/src/Booking/models/Booking';
import { SitterBookingWithDetails } from '../../../happypaws-shared/src/Booking/models/BookingWithDetails';
import { useGetSitterBookings } from '../../../hooks/bookings/useGetSitterBookings';
import { useGetSitterProfile } from '../../../hooks/sitter/useGetSitterProfile';
import { useSetAvailability } from '../../../hooks/sitter/useSetAvailability';
import { formatMonthYear, generateCalendarGrid, isSameDay } from '../../../utils/calendarUtils';
import CalendarCell from './components/CalendarCell';
import Legend from './components/Legend';
import MonthStats from './components/MonthStats';
import QuickActions from './components/QuickActions';
import UpcomingEvents from './components/UpcomingEvents';

export interface CalendarBooking {
  id: string;
  petName: string;
  service: string;
  startDate: Dayjs;
  endDate: Dayjs;
  status: BookingStatus;
  price: number;
}

export interface UnavailableDate {
  date: Dayjs;
}

type CalendarMode = 'toggle' | 'block' | 'available';

import SitterHeader from '../SitterHeader/SitterHeader';

const SitterCalendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  // const [view, setView] = useState<'month'>('month');
  const [mode, setMode] = useState<CalendarMode>('toggle');

  const { data: sitterProfile } = useGetSitterProfile();
  const { data: sitterBookings } = useGetSitterBookings();
  const { mutate: setAvailability } = useSetAvailability();

  // Map real availability to calendar format
  const unavailableDates = useMemo<UnavailableDate[]>(() => {
    if (!sitterProfile?.availability) return [];
    return sitterProfile.availability
      .filter((a) => !a.isAvailable)
      .map((a) => ({ date: dayjs(a.date) }));
  }, [sitterProfile]);

  // Map real bookings to calendar format
  const bookings = useMemo<CalendarBooking[]>(() => {
    if (!sitterBookings) return [];
    return sitterBookings.map((b: SitterBookingWithDetails) => ({
      id: b.id,
      petName: b.pets.map((p) => p.petName).join(', '),
      service: b.serviceType,
      startDate: dayjs(b.startDate),
      endDate: dayjs(b.endDate),
      status: b.status as BookingStatus,
      price: Number(b.totalPrice),
    }));
  }, [sitterBookings]);

  const year = currentDate.year();
  const month = currentDate.month();
  const calendarGrid = generateCalendarGrid(year, month);

  const handlePrevMonth = () => setCurrentDate(currentDate.subtract(1, 'month'));
  const handleNextMonth = () => setCurrentDate(currentDate.add(1, 'month'));

  const handleDayClick = (date: Dayjs) => {
    const dateStr = date.format('YYYY-MM-DD');
    const isCurrentlyUnavailable = unavailableDates.some((d) => isSameDay(d.date, date));

    let isAvailable: boolean;
    if (mode === 'block') {
      isAvailable = false;
    } else if (mode === 'available') {
      isAvailable = true;
    } else {
      // toggle
      isAvailable = isCurrentlyUnavailable;
    }

    setAvailability({ dates: [{ date: dateStr, isAvailable }] });
  };

  const getBookingsForDate = (date: Dayjs): CalendarBooking[] => {
    return bookings.filter((booking) => {
      const start = booking.startDate.startOf('day');
      const end = booking.endDate.endOf('day');
      const check = date.startOf('day');
      return (
        (check.isAfter(start) || check.isSame(start)) && (check.isBefore(end) || check.isSame(end))
      );
    });
  };

  const isDateUnavailable = (date: Dayjs): boolean =>
    unavailableDates.some((d) => isSameDay(d.date, date));

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <SitterHeader />
      <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Calendar
      </Typography>

      {/* Mode indicator */}
      {mode !== 'toggle' && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: mode === 'block' ? '#ffebee' : '#e8f5e9',
            border: `1px solid ${mode === 'block' ? '#ef9a9a' : '#a5d6a7'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {mode === 'block' ? 'Click dates to block them' : 'Click dates to mark as available'}
          </Typography>
          <Button size="small" onClick={() => setMode('toggle')} sx={{ textTransform: 'none' }}>
            Done
          </Button>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            {/* Calendar Header */}
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={handlePrevMonth} size="small">
                  <ChevronLeft />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 700, minWidth: 150 }}>
                  {formatMonthYear(year, month)}
                </Typography>
                <IconButton onClick={handleNextMonth} size="small">
                  <ChevronRight />
                </IconButton>
              </Box>
              {/* <ButtonGroup size="small">
                <Button
                  variant={view === 'month' ? 'contained' : 'outlined'}
                  onClick={() => setView('month')}
                  sx={{ borderRadius: '25px 0 0 25px', textTransform: 'none', fontWeight: 600 }}
                >
                  Month
                </Button>
                <Button
                  variant={view === 'week' ? 'contained' : 'outlined'}
                  onClick={() => setView('week')}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                  disabled
                >
                  Week
                </Button>
                <Button
                  variant={view === 'day' ? 'contained' : 'outlined'}
                  onClick={() => setView('day')}
                  sx={{ borderRadius: '0 25px 25px 0', textTransform: 'none', fontWeight: 600 }}
                  disabled
                >
                  Day
                </Button>
              </ButtonGroup> */}
            </Box>

            {/* Week Day Headers */}
            <Grid container spacing={1} sx={{ mb: 1 }}>
              {weekDays.map((day) => (
                <Grid size={{ xs: 12 / 7 }} key={day}>
                  <Typography
                    variant="subtitle2"
                    align="center"
                    sx={{ fontWeight: 700, color: 'text.secondary' }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* Calendar Grid */}
            <Grid container spacing={1}>
              {calendarGrid.map((day, index) => (
                <Grid size={{ xs: 12 / 7 }} key={index}>
                  <CalendarCell
                    dayNumber={day.dayNumber}
                    isCurrentMonth={day.isCurrentMonth}
                    isToday={day.isToday}
                    date={day.date}
                    bookings={getBookingsForDate(day.date)}
                    isUnavailable={isDateUnavailable(day.date)}
                    onClick={handleDayClick}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Legend />
          <QuickActions
            onBlock={() => setMode('block')}
            onSetAvailable={() => setMode('available')}
          />
          <MonthStats bookings={bookings} />
          <UpcomingEvents bookings={bookings} />
        </Grid>
      </Grid>
    </Container>
    </Box>
  );
};

export default SitterCalendar;
